import { useState, useMemo, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import {
  useMediaQuery,
  IconButton,
  MenuItem,
  Box,
  Button,
} from '@mui/material';
import { EmbeddableMenu } from '@/integrations/EmbeddableMenu';
import { isEmbeddableWidgetBundle } from '@/integrations/embeddableWidgetPortalUtils';
import AddIcon from '@mui/icons-material/Add';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import StyledButton from '@/components/common/styledButton';
import PortfolioAccountsTable from '@/modules/portfolio/grids/accountsGrid';
import AddAccountModal from '@/modules/portfolio/addAccountModal';
import { ManageAccountGroupsModal } from '@/modules/portfolio/ManageAccountGroupsModal';
import { ManageAccountPermissionsModal } from '@/modules/portfolio/ManageAccountPermissionsModal';
import ConfirmationDialog from '@/components/common/ConfirmationDialog';
import MarkAsClosedDialog from '@/modules/portfolio/dialogs/MarkAsClosedDialog';
import UploadAccountHistoryModal from '@/modules/portfolio/dialogs/UploadAccountHistoryModal';
import EditAccountModal from '@/modules/portfolio/dialogs/EditAccountModal';
import ClosedAccountsModal from '@/modules/portfolio/dialogs/ClosedAccountsModal';
import AccountDetailsFlyout from '@/components/flyouts/AccountDetailsFlyout';
import { StandardWidgetProps } from '@/models/WidgetModels';
import { useModuleAccess } from '@/permissions';
import { useControlledDates } from '@/hooks/useControlledDates';
import DateRange from '@/components/date/DateRange';
import AsOfDatePicker from '@/components/date/AsOfDatePicker';
import { useNavigate, useOutletContext } from 'react-router';
import WidgetHeader, { HeaderIcon } from '@/components/common/WidgetHeader';
import { DataRangeFilterEnum, getDateRangeFromFilter } from '@/constants/dates';
import { ViewMode } from '@/constants/enum';
import { dashboardMobileWidthValue, mobileWidth } from '@/constants';
import { minimalScrollbarStyling } from '@/constants/styles';
import { useWidgetWidthIsBelow } from '@/hooks/useWidgetWidthIsBelow';
import {
  accountsApi,
  useGetAccountsQuery,
  useGetAccountTagsQuery,
  useDeleteAccountMutation,
  useUpdateAccountTagsMutation,
} from '@/store/accountsApi';
import { holdingsApi } from '@/store/holdingsApi';
import { transactionsApi } from '@/store/transactionsApi';
import { cashFlowApi } from '@/store/cashFlowApi';
import { skipToken } from '@reduxjs/toolkit/query';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import { useCanAddAccounts } from '@/hooks/useCanAddAccounts';
import CategoryBarChart from '@/components/charts/CategoryBarChart';
import AccountsHistoricalGrid from '@/components/grids/historical/AccountsHistoricalGrid';
import HistoricalGrid from '@/components/grids/historical/HistoricalGrid';
import HistoricalTrendView from '@/components/grids/historical/HistoricalTrendView';
import HistoricalTrendChart from '@/components/charts/historicalTrend/HistoricalTrendChart';
import {
  trendGroupConfigFor,
  trendGroupNameFor,
} from '@/components/charts/historicalTrend/historicalTrendData';
import { AccountBase } from '@/models/AccountViewModel';
import { useLocalization } from '@/hooks/useLocalization';
import { useCanNavigateTo } from '@/hooks/useCanNavigateTo';
import { useEffectiveExclusions } from '@/hooks/useEffectiveExclusions';
import { Capacitor } from '@capacitor/core';
import { usePageDefaults } from '@/hooks/usePageDefaults';
import { useExplicitPageState } from '@/hooks/useExplicitPageState';
import { GroupBySelect, GroupOption } from '@/components/common/GroupBySelect';
import { APP_DATE_FORMAT } from '@/constants/dateFormats';
import dayjs from '@/config/dayjs';
import { DASHBOARD_WIDGET_DATE_PICKER_SX } from '@/constants/dashboardWidgetDatePickerSx';
import { PickerValue } from '@mui/x-date-pickers/internals';
import { ContentContainer } from '../contentContainer';

interface AccountGroupOption extends GroupOption {
  isCustom?: boolean;
}

interface AccountCategory {
  name: string;
  accounts: AccountBase[];
  total: number;
  institutionId: string;
}

const SHARED_ACCOUNT_ACCESS_PATH = '/ManageAccounts/SharedAccountAccess';

/** Displayed as a widget and page under Portfolio > Accounts */
export const PortfolioAccounts = ({
  isWidget = false,
  startDate: propStartDate,
  endDate: propEndDate,
  onDataLoaded,
  excludedAccounts,
  excludedAccountTypes,
  excludedCategories,
  title,
  container = 'page',
}: StandardWidgetProps) => {
  const { t } = useLocalization();
  const widgetTitle = title ?? t('accounts');
  const { validateByCode } = useModuleAccess();
  const canUseAggregation = validateByCode('CanUseAggregation');
  const canAddAccounts = useCanAddAccounts();
  const outletContext = useOutletContext<{
    startDate?: string;
    endDate?: string;
    outletSetShowDateRange?: (show: boolean) => void;
    excludedAccounts?: number[];
    excludedCategories?: number[];
    excludedAccountTypes?: number[];
    excludedInstitutions?: number[];
    excludedTags?: number[];
    excludedGroups?: Record<string, string[]>;
  } | null>();
  const outletStartDate = outletContext?.startDate;
  const outletEndDate = outletContext?.endDate;
  const outletSetShowDateRange = outletContext?.outletSetShowDateRange;
  const outletExcludedAccounts = outletContext?.excludedAccounts;
  const outletExcludedCategories = outletContext?.excludedCategories;
  const outletExcludedAccountTypes = outletContext?.excludedAccountTypes;
  const outletExcludedInstitutions = outletContext?.excludedInstitutions;
  const outletExcludedTags = outletContext?.excludedTags;
  const outletExcludedGroups = outletContext?.excludedGroups;
  const effectiveExclusions = useEffectiveExclusions({
    excludedAccounts: excludedAccounts || outletExcludedAccounts,
    excludedCategories: excludedCategories || outletExcludedCategories,
    excludedAccountTypes: excludedAccountTypes || outletExcludedAccountTypes,
    excludedInstitutions: outletExcludedInstitutions,
  });

  const { ref: widgetWidthRef, isBelow: isWidgetBelowNarrowDateField } =
    useWidgetWidthIsBelow(isWidget, dashboardMobileWidthValue);
  const widgetNarrowDateField = isWidget && isWidgetBelowNarrowDateField;

  // Get firm-configured default grouping from menu settings
  const { defaultGroup: firmDefaultGroup, defaultView: firmDefaultView } =
    usePageDefaults('/Portfolio/Accounts', 'accounts');

  // Define group options using translations
  const GROUP_OPTIONS: AccountGroupOption[] = useMemo(
    () => [
      { value: 'none', label: t('noGroups') },
      { value: 'institution', label: t('institution') },
      { value: 'data source', label: t('dataSource') },
    ],
    [t],
  );

  // View mode options for persistence
  const VIEW_OPTIONS = useMemo(
    () => [ViewMode.Table, ViewMode.Chart, ViewMode.Historical],
    [],
  );

  const isMobile = useMediaQuery(mobileWidth);
  /** Widget header row: date + add + actions — compact 44px controls (viewport mobile or narrow tile). */
  const compactWidgetHeaderRowActions =
    isWidget && (isMobile || widgetNarrowDateField);
  const [selectedAccount, setSelectedAccount] = useState<AccountBase | null>(
    null,
  );
  const navigate = useNavigate();
  const canViewSharedAccountAccess = useCanNavigateTo(
    SHARED_ACCOUNT_ACCESS_PATH,
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [manageGroupsOpen, setManageGroupsOpen] = useState(false);
  const [managePermissionsOpen, setManagePermissionsOpen] = useState(false);
  const [closedAccountsOpen, setClosedAccountsOpen] = useState(false);
  const [
    pendingClosedAccountFlyoutAccount,
    setPendingClosedAccountFlyoutAccount,
  ] = useState<AccountBase | null>(null);
  const [closedAccountFlyoutAccount, setClosedAccountFlyoutAccount] =
    useState<AccountBase | null>(null);
  const [pendingClosedAccountDeleteId, setPendingClosedAccountDeleteId] =
    useState<number | null>(null);
  const [deletedClosedAccountId, setDeletedClosedAccountId] = useState<
    number | null
  >(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [markAsClosedDialogOpen, setMarkAsClosedDialogOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [uploadHistoryOpen, setUploadHistoryOpen] = useState(false);

  const [deleteAccount, { isLoading: isDeleting }] = useDeleteAccountMutation();
  const [updateAccount, { isLoading: isUpdating }] =
    useUpdateAccountTagsMutation();

  const dispatch = useDispatch();

  const menuRef = useRef<HTMLButtonElement | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const handleToggle = () => {
    setIsMenuOpen((prev) => !prev);
  };

  const handleMenuClose = () => {
    setIsMenuOpen(false);
  };

  const isNative = Capacitor.getPlatform() !== 'web';

  const handleAddAccountModalClose = () => {
    setDialogOpen(false);
    dispatch(
      accountsApi.util.invalidateTags([{ type: 'Accounts', id: 'LIST' }]),
    );
  };

  const handleManageGroupsClick = () => {
    setManageGroupsOpen(true);
    handleMenuClose();
  };

  const handleManagePermissionsClick = () => {
    setManagePermissionsOpen(true);
    handleMenuClose();
  };

  const handleSharedAccessClick = () => {
    void navigate(SHARED_ACCOUNT_ACCESS_PATH);
    handleMenuClose();
  };

  const handleViewClosedAccountsClick = () => {
    setClosedAccountsOpen(true);
    handleMenuClose();
  };

  const handleClosedAccountSelect = (account: AccountBase) => {
    setPendingClosedAccountFlyoutAccount(account);
    setClosedAccountsOpen(false);
  };

  const handleClosedAccountFlyoutClose = () => {
    setClosedAccountFlyoutAccount(null);
    setClosedAccountsOpen(true);
  };

  const handleClosedAccountsClose = () => {
    setPendingClosedAccountFlyoutAccount(null);
    setClosedAccountFlyoutAccount(null);
    setPendingClosedAccountDeleteId(null);
    setClosedAccountsOpen(false);
  };

  const handleManagePermissionsClose = () => {
    setManagePermissionsOpen(false);
  };

  const authLoaded = useAuthIsLoaded();

  useEffect(() => {
    if (!pendingClosedAccountFlyoutAccount || closedAccountsOpen) {
      return;
    }

    const frameId = window.requestAnimationFrame(() => {
      setClosedAccountFlyoutAccount(pendingClosedAccountFlyoutAccount);
      setPendingClosedAccountFlyoutAccount(null);
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [closedAccountsOpen, pendingClosedAccountFlyoutAccount]);
  const { data: accountTags, isLoading: areTagsLoading } =
    useGetAccountTagsQuery({});
  const defaultRange = getDateRangeFromFilter(DataRangeFilterEnum.OpenYear);

  const { startDate, endDate, setStartDate, setEndDate } = useControlledDates({
    propStartDate: outletStartDate ? outletStartDate : propStartDate,
    propEndDate: outletEndDate ? outletEndDate : propEndDate,
    isWidget,
    initialWidgetStartDate: defaultRange.startDate,
    initialWidgetEndDate: defaultRange.endDate,
  });

  const handleWidgetDateRangeChange = (nextStart: string, nextEnd: string) => {
    setStartDate(nextStart);
    setEndDate(nextEnd);
  };

  const excludedGroupsApi = useMemo(() => {
    const groups: Record<string, string[]> | undefined = outletExcludedGroups;
    if (!groups || Object.keys(groups).length === 0) return undefined;
    return Object.entries(groups)
      .filter(([, ids]: [string, string[]]) => ids.length > 0)
      .map(([GroupId, ExcludedIds]: [string, string[]]) => ({
        GroupId,
        ExcludedIds,
      }));
  }, [outletExcludedGroups]);

  const { data: rawAccountsData } = useGetAccountsQuery(
    authLoaded
      ? {
          showClosed: false,
          asOfDate: endDate || '',
          ...(effectiveExclusions.excludedAccounts.length > 0
            ? { ExcludedAccounts: effectiveExclusions.excludedAccounts }
            : {}),
          ...(effectiveExclusions.excludedAccountTypes.length > 0
            ? { ExcludedAccountTypes: effectiveExclusions.excludedAccountTypes }
            : {}),
          ...(effectiveExclusions.excludedCategories.length > 0
            ? { ExcludedCategories: effectiveExclusions.excludedCategories }
            : {}),
          ...(effectiveExclusions.excludedInstitutions.length > 0
            ? { ExcludedInstitutions: effectiveExclusions.excludedInstitutions }
            : {}),
          ...(outletExcludedTags && outletExcludedTags.length > 0
            ? { ExcludedTags: outletExcludedTags }
            : {}),
          ...(excludedGroupsApi && excludedGroupsApi.length > 0
            ? { ExcludedGroups: excludedGroupsApi }
            : {}),
        }
      : skipToken,
  );
  const accountsData = rawAccountsData;

  const handleViewModeChange = (newViewMode: ViewMode) => {
    // When switching to Historical view, "data source" grouping is not supported
    if (
      newViewMode === ViewMode.Historical &&
      activeGroup.value === 'data source'
    ) {
      const noGroupOption = allGroupOptions.find((opt) => opt.value === 'none');
      if (noGroupOption) {
        setActiveGroup(noGroupOption);
      }
    }
    setViewMode(newViewMode);
  };

  const userGeneratedGroups = useMemo(() => {
    if (!accountTags) return [];
    return accountTags.map((group) => {
      const prefix = group.IsAdvisorTag ? 'advisor' : 'investor';
      const valueProperty = `${prefix}:${String(group.TagCategoryId)}`;
      return {
        value: valueProperty,
        label: group.Name,
        isCustom: true,
        TagCategoryId: group.TagCategoryId,
        title: group.Name,
        valueProperty: valueProperty,
        displayProperty: valueProperty,
        description: group.Description,
      };
    });
  }, [accountTags]);

  const allGroupOptions = useMemo(() => {
    return [...GROUP_OPTIONS, ...userGeneratedGroups];
  }, [GROUP_OPTIONS, userGeneratedGroups]);

  // Use explicit page state for grouping and view mode
  // Changes are session-only until explicitly saved
  //
  // NOTE (2/2/26): Product decided to disable user-level view default persistence.
  // The hook provides saveViewAsDefault and resetViewToFirmDefault, but we intentionally
  // do not destructure or expose them. View selection is session-only; users cannot
  // save their preferred view as a default. The functionality is preserved in the hook
  // for potential future re-enablement.
  const {
    currentGroup: activeGroup,
    setCurrentGroup: setActiveGroup,
    currentView: viewMode,
    setCurrentView: setViewMode,
    savedGroupValue,
    hasUnsavedGroupChange,
    isUsingFirmDefaultGroup,
    saveGroupAsDefault,
    resetGroupToFirmDefault,
    // View persistence disabled per product decision (2/2/26):
    // saveViewAsDefault,
    // resetViewToFirmDefault,
  } = useExplicitPageState({
    pageKey: 'Accounts',
    groupOptions: allGroupOptions,
    viewOptions: VIEW_OPTIONS,
    firmDefaultGroup,
    firmDefaultView,
    getGroupValue: (g) => g.value,
    getViewValue: (v) => v,
  });

  // Update outlet date range visibility based on view mode
  useEffect(() => {
    if (outletSetShowDateRange) {
      if (viewMode === ViewMode.Historical) {
        outletSetShowDateRange(true);
      } else {
        outletSetShowDateRange(false);
      }
    }
  }, [viewMode, outletSetShowDateRange]);

  const filteredGroupOptions: AccountGroupOption[] = useMemo(() => {
    if (viewMode === ViewMode.Historical) {
      return allGroupOptions.filter((option) => option.value !== 'data source');
    }
    return allGroupOptions;
  }, [viewMode, allGroupOptions]);

  const balanceData = useMemo(() => {
    if (!accountsData) return undefined;

    // Calculate totals first
    let totalAssets = 0;
    let totalLiabilities = 0;
    accountsData.Records.forEach((account: AccountBase) => {
      if (account.IsAsset) {
        totalAssets += account.Balance;
      } else {
        totalLiabilities += account.Balance;
      }
    });

    let assetCategories, liabilityCategories;

    const isCustomGroup =
      activeGroup.value.startsWith('investor:') ||
      activeGroup.value.startsWith('advisor:');

    if (isCustomGroup) {
      const filteredAccounts = accountsData.Records.filter(
        (account: AccountBase) => {
          return Object.prototype.hasOwnProperty.call(
            account.Tags,
            activeGroup.value,
          );
        },
      );

      const assetsByTag = new Map<string, AccountCategory>();
      const liabilitiesByTag = new Map<string, AccountCategory>();

      filteredAccounts.forEach((account: AccountBase) => {
        const tagName = account.Tags[activeGroup.value] || t('notSet');

        if (account.IsAsset) {
          if (!assetsByTag.has(tagName)) {
            assetsByTag.set(tagName, {
              name: tagName,
              accounts: [],
              total: 0,
              institutionId: `custom-group-asset-${tagName}`,
            });
          }
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const category = assetsByTag.get(tagName)!;
          category.accounts.push(account);
          category.total += account.Balance;
        } else {
          if (!liabilitiesByTag.has(tagName)) {
            liabilitiesByTag.set(tagName, {
              name: tagName,
              accounts: [],
              total: 0,
              institutionId: `custom-group-liability-${tagName}`,
            });
          }
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const category = liabilitiesByTag.get(tagName)!;
          category.accounts.push(account);
          category.total += account.Balance;
        }
      });

      assetCategories = Array.from(assetsByTag.values()).sort(
        (a, b) => b.total - a.total,
      );
      liabilityCategories = Array.from(liabilitiesByTag.values()).sort(
        (a, b) => b.total - a.total,
      );
    } else if (activeGroup.value === 'institution') {
      const assetsByInstitution = new Map<string, AccountCategory>();
      const liabilitiesByInstitution = new Map<string, AccountCategory>();

      accountsData.Records.forEach((account: AccountBase) => {
        let institutionId, institutionName;

        if (account.AccountType === 2) {
          institutionId = 'manually-entered';
          institutionName = t('manuallyEntered');
        } else if (account.AccountType === 3) {
          institutionId = account.InstitutionId.toString();
          institutionName = account.InstitutionName
            ? `${account.InstitutionName} (${t('fromEstate')})`
            : t('fromEstate');
        } else if (account.UseZillow) {
          institutionId = 'zillow';
          institutionName = t('updatedByZillowZestimate');
        } else {
          institutionId = account.InstitutionId.toString();
          institutionName = account.InstitutionName || t('noInstitution');
        }

        if (account.IsAsset) {
          if (!assetsByInstitution.has(institutionId)) {
            assetsByInstitution.set(institutionId, {
              name: institutionName,
              accounts: [],
              total: 0,
              institutionId: institutionId,
            });
          }
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const category = assetsByInstitution.get(institutionId)!;
          category.accounts.push(account);
          category.total += account.Balance;
        } else {
          if (!liabilitiesByInstitution.has(institutionId)) {
            liabilitiesByInstitution.set(institutionId, {
              name: institutionName,
              accounts: [],
              total: 0,
              institutionId: institutionId,
            });
          }
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const category = liabilitiesByInstitution.get(institutionId)!;
          category.accounts.push(account);
          category.total += account.Balance;
        }
      });
      assetCategories = Array.from(assetsByInstitution.values()).sort(
        (a, b) => b.total - a.total,
      );
      liabilityCategories = Array.from(liabilitiesByInstitution.values()).sort(
        (a, b) => b.total - a.total,
      );
    } else if (activeGroup.value === 'data source') {
      const assetsByDataSource = new Map<string, AccountCategory>();
      const liabilitiesByDataSource = new Map<string, AccountCategory>();

      accountsData.Records.forEach((account: AccountBase) => {
        let dataSourceId, dataSourceName;
        if (account.UseZillow) {
          dataSourceId = 'zillow';
          dataSourceName = t('zillow');
        } else {
          switch (account.AccountType) {
            case 2:
              dataSourceId = 'manual';
              dataSourceName = t('manuallyEnteredAccounts');
              break;
            case 3:
              dataSourceId = 'estate';
              dataSourceName = t('estate');
              break;
            case 4:
              dataSourceId = 'allocation';
              dataSourceName = t('allocation');
              break;
            case 8:
              dataSourceId = 'advisor';
              dataSourceName = t('managedByAdvisor');
              break;
            default:
              dataSourceId = 'aggregated';
              dataSourceName = t('linkedAccounts');
              break;
          }
        }

        if (account.IsAsset) {
          if (!assetsByDataSource.has(dataSourceId)) {
            assetsByDataSource.set(dataSourceId, {
              name: dataSourceName,
              accounts: [],
              total: 0,
              institutionId: dataSourceId,
            });
          }
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const category = assetsByDataSource.get(dataSourceId)!;
          category.accounts.push(account);
          category.total += account.Balance;
        } else {
          if (!liabilitiesByDataSource.has(dataSourceId)) {
            liabilitiesByDataSource.set(dataSourceId, {
              name: dataSourceName,
              accounts: [],
              total: 0,
              institutionId: dataSourceId,
            });
          }
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const category = liabilitiesByDataSource.get(dataSourceId)!;
          category.accounts.push(account);
          category.total += account.Balance;
        }
      });

      assetCategories = Array.from(assetsByDataSource.values()).sort(
        (a, b) => b.total - a.total,
      );
      liabilityCategories = Array.from(liabilitiesByDataSource.values()).sort(
        (a, b) => b.total - a.total,
      );
    } else {
      // For "none", create individual categories for each account
      assetCategories = accountsData.Records.filter(
        (account: AccountBase) => account.IsAsset,
      )
        .map((account: AccountBase) => ({
          name: account.Name,
          accounts: [account],
          total: account.Balance,
          institutionId: account.InstitutionId.toString(),
        }))
        .sort(
          (a: { total: number }, b: { total: number }) => b.total - a.total,
        );

      liabilityCategories = accountsData.Records.filter(
        (account: AccountBase) => !account.IsAsset,
      )
        .map((account: AccountBase) => ({
          name: account.Name,
          accounts: [account],
          total: account.Balance,
          institutionId: account.InstitutionId.toString(),
        }))
        .sort(
          (a: { total: number }, b: { total: number }) => b.total - a.total,
        );
    }

    return {
      assets: {
        total: totalAssets,
        categories: assetCategories,
      },
      liabilities: {
        total: totalLiabilities,
        categories: liabilityCategories,
      },
      totalLiabilitiesAndEquity: totalAssets,
    };
  }, [t, accountsData, activeGroup]);

  const handlePrint = () => {
    window.print();
  };

  const handleGroupChange = (option: AccountGroupOption) => {
    setActiveGroup(option);
  };
  const handleDeleteConfirm = () => {
    const accountId = selectedAccount?.AccountId;
    if (accountId == null || accountId === 0) {
      console.error(
        'Delete confirmed but no account selected; closing dialog. This should not happen when opening delete from the row actions menu.',
      );
      setDeleteDialogOpen(false);
      return;
    }
    deleteAccount(accountId)
      .unwrap()
      .then(() => {
        if (pendingClosedAccountDeleteId === accountId) {
          setDeletedClosedAccountId(accountId);
          setPendingClosedAccountDeleteId(null);
        }

        dispatch(holdingsApi.util.invalidateTags(['Holdings']));
        dispatch(
          transactionsApi.util.invalidateTags([
            'TransactionTrends',
            'Transactions',
            'BrokerageTransactions',
            'BankTransactionsOverview',
            'BankTransactions',
            'WizardEligibility',
          ]),
        );
        dispatch(cashFlowApi.util.invalidateTags(['CashFlowCategories']));
        setDeleteDialogOpen(false);
      })
      .catch((error: unknown) => {
        console.error('Failed to delete account:', error);
        setDeleteDialogOpen(false);
      });
  };

  const handleDeleteDialogClose = () => {
    setPendingClosedAccountDeleteId(null);
    setDeleteDialogOpen(false);
  };

  const handleMarkAsClosedConfirm = (closedDate: PickerValue) => {
    if (!closedDate) return;

    updateAccount({
      ...selectedAccount,
      ClosedDate: dayjs(closedDate).format(APP_DATE_FORMAT),
    } as AccountBase)
      .unwrap()
      .then(() => {
        setMarkAsClosedDialogOpen(false);
      })
      .catch((error: unknown) => {
        console.error('Failed to mark account as closed:', error);
        setMarkAsClosedDialogOpen(false);
      });
  };

  const headerIcons: HeaderIcon[] = [];
  const accountActions = (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: 1,
        flexWrap: 'wrap',
      }}
    >
      {isWidget && viewMode === ViewMode.Chart && !isNative && !isMobile && (
        <IconButton
          onClick={handlePrint}
          sx={{
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: '4px',
            boxShadow: (theme) => theme.shadows[1],
          }}
        >
          <PrintOutlinedIcon />
        </IconButton>
      )}
      {canUseAggregation && (
        <StyledButton
          variant='contained'
          startIcon={<AddIcon />}
          aria-label={t('addAccount')}
          disabled={!canAddAccounts}
          onClick={() => {
            if (!canAddAccounts) {
              return;
            }
            setDialogOpen(true);
          }}
          sx={(theme) => ({
            ...(compactWidgetHeaderRowActions
              ? {
                  minHeight: 44,
                  height: 44,
                  minWidth: 44,
                  width: 44,
                  p: 0,
                  boxSizing: 'border-box',
                  '& .MuiButton-icon': {
                    margin: 0,
                    display: 'flex',
                  },
                }
              : {
                  [theme.breakpoints.down('sm')]: {
                    padding: '10px !important',
                  },
                  '& .MuiButton-icon': {
                    margin: '0px auto',
                    display: 'block',
                  },
                }),
          })}
        >
          {isMobile || compactWidgetHeaderRowActions ? t('') : t('addAccount')}
        </StyledButton>
      )}
      {compactWidgetHeaderRowActions ? (
        <Button
          variant='outlined'
          color='inherit'
          size='large'
          type='button'
          aria-label='more'
          id='long-button'
          ref={menuRef}
          aria-controls={isMenuOpen ? 'long-menu' : undefined}
          aria-expanded={isMenuOpen ? 'true' : undefined}
          aria-haspopup='true'
          onClick={handleToggle}
          sx={(theme) => ({
            minWidth: 44,
            minHeight: 44,
            width: 44,
            height: 44,
            p: 0,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxSizing: 'border-box',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '8px',
            color: 'text.primary',
            '&:hover': {
              borderColor: theme.palette.text.secondary,
              backgroundColor: theme.palette.action.hover,
            },
          })}
        >
          <MoreVertIcon fontSize='medium' />
        </Button>
      ) : (
        <IconButton
          aria-label='more'
          id='long-button'
          ref={menuRef}
          aria-controls={isMenuOpen ? 'long-menu' : undefined}
          aria-expanded={isMenuOpen ? 'true' : undefined}
          aria-haspopup='true'
          onClick={handleToggle}
          sx={{
            marginLeft: 0,
            padding: 0.85,
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: '8px',
          }}
        >
          <MoreVertIcon />
        </IconButton>
      )}

      <EmbeddableMenu
        id='long-menu'
        slotProps={{
          list: {
            'aria-labelledby': 'long-button',
          },
        }}
        anchorEl={menuRef.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={isMenuOpen}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleManageGroupsClick}>
          {t('manageCustomAccountGroups')}
        </MenuItem>

        {!isEmbeddableWidgetBundle && (
          <MenuItem onClick={handleManagePermissionsClick}>
            {t('manageAccountPermissions')}
          </MenuItem>
        )}

        {!isEmbeddableWidgetBundle && canViewSharedAccountAccess && (
          <MenuItem onClick={handleSharedAccessClick}>
            {t('sharedAccountAccess')}
          </MenuItem>
        )}

        <MenuItem onClick={handleViewClosedAccountsClick}>
          {t('closedAccounts.viewClosedAccounts', 'View Closed Accounts')}
        </MenuItem>
      </EmbeddableMenu>
    </Box>
  );

  const accountsTrendSeries = useMemo(
    () =>
      [
        {
          trendType: 'grouped',
          groupConfig: trendGroupConfigFor(activeGroup.value),
          renameSeries: (name: string) =>
            trendGroupNameFor(activeGroup.value, name, {
              noInstitution: t('noInstitution'),
              notSet: t('notSet'),
            }),
        },
      ] as const,
    [activeGroup.value, t],
  );

  const historicalGrid = isMobile ? (
    <AccountsHistoricalGrid
      dateFilter={DataRangeFilterEnum.OpenYear}
      startDate={startDate || defaultRange.startDate}
      endDate={endDate || defaultRange.endDate}
      isWidget={isWidget}
      groupBy={activeGroup.value}
      excludedAccounts={effectiveExclusions.excludedAccounts}
      excludedAccountTypes={effectiveExclusions.excludedAccountTypes}
      excludedCategories={effectiveExclusions.excludedCategories}
      excludedInstitutions={effectiveExclusions.excludedInstitutions}
    />
  ) : (
    <HistoricalGrid
      dateFilter={DataRangeFilterEnum.OpenYear}
      startDate={startDate || defaultRange.startDate}
      endDate={endDate || defaultRange.endDate}
      isWidget={isWidget}
      groupBy={activeGroup.value}
      excludedAccounts={effectiveExclusions.excludedAccounts}
      excludedAccountTypes={effectiveExclusions.excludedAccountTypes}
      excludedCategories={effectiveExclusions.excludedCategories}
      excludedInstitutions={effectiveExclusions.excludedInstitutions}
    />
  );

  return (
    <ContentContainer container={container}>
      <Box
        ref={widgetWidthRef}
        sx={{
          '@media print': {
            paddingX: 0,
            paddingTop: 0,
            paddingBottom: 0,
            gap: 0,
          },
        }}
      >
        <WidgetHeader
          title={widgetTitle}
          headerIcons={headerIcons}
          viewMode={{
            currentViewMode: viewMode,
            setViewMode: handleViewModeChange,
            viewModes: [ViewMode.Table, ViewMode.Chart, ViewMode.Historical],
          }}
          leftContent={
            <Box sx={{ marginTop: 3, display: 'block' }}>
              <GroupBySelect
                label={t('customGroups')}
                options={filteredGroupOptions}
                activeOption={activeGroup}
                onOptionChange={handleGroupChange}
                savedGroupValue={savedGroupValue}
                firmDefaultGroup={firmDefaultGroup}
                hasUnsavedGroupChange={hasUnsavedGroupChange}
                isUsingFirmDefaultGroup={isUsingFirmDefaultGroup}
                onSaveAsDefault={saveGroupAsDefault}
                onResetToFirmDefault={resetGroupToFirmDefault}
                disabled={areTagsLoading}
                minWidth={160}
              />
            </Box>
          }
          rightContent={
            isWidget ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                  justifyContent: 'flex-end',
                }}
              >
                <Box sx={DASHBOARD_WIDGET_DATE_PICKER_SX}>
                  {viewMode === ViewMode.Historical ? (
                    <DateRange
                      startDate={startDate}
                      endDate={endDate}
                      onChange={handleWidgetDateRangeChange}
                      useWidgetNarrowIconField={widgetNarrowDateField}
                    />
                  ) : (
                    <AsOfDatePicker
                      date={endDate}
                      onChange={setEndDate}
                      label={t('asOf')}
                      useWidgetNarrowIconField={widgetNarrowDateField}
                    />
                  )}
                </Box>
                {accountActions}
              </Box>
            ) : (
              <Box>{accountActions}</Box>
            )
          }
        />

        <Box
          sx={{
            ...minimalScrollbarStyling,
          }}
        >
          {viewMode === ViewMode.Table && (
            <PortfolioAccountsTable
              endDate={endDate || ''}
              isWidget={isWidget}
              useExternalScrollContainer={isWidget}
              groupBy={activeGroup.value}
              excludedAccounts={effectiveExclusions.excludedAccounts}
              excludedAccountTypes={effectiveExclusions.excludedAccountTypes}
              excludedCategories={effectiveExclusions.excludedCategories}
              excludedInstitutions={effectiveExclusions.excludedInstitutions}
              excludedTags={outletExcludedTags}
              excludedGroups={outletExcludedGroups}
              onDataLoaded={onDataLoaded}
              handleEdit={(account) => {
                setSelectedAccount(account);
                setEditModalOpen(true);
              }}
              handleMarkAsClosed={(account) => {
                setSelectedAccount(account);
                setMarkAsClosedDialogOpen(true);
              }}
              handleUploadHistory={(account) => {
                setSelectedAccount(account);
                setUploadHistoryOpen(true);
              }}
              handleDelete={(account) => {
                setSelectedAccount(account);
                setDeleteDialogOpen(true);
              }}
              handleAccountSelect={setSelectedAccount}
            />
          )}
          {viewMode === ViewMode.Chart && balanceData && (
            <CategoryBarChart
              balanceData={balanceData}
              dateFilter={DataRangeFilterEnum.OpenYear}
              enableDrilldown={activeGroup.value !== 'none'}
              isWidget={isWidget}
            />
          )}
          {viewMode === ViewMode.Historical &&
            (isWidget ? (
              historicalGrid
            ) : (
              <HistoricalTrendView
                table={historicalGrid}
                chart={
                  <HistoricalTrendChart
                    startDate={startDate || defaultRange.startDate}
                    endDate={endDate || defaultRange.endDate}
                    seriesRequests={accountsTrendSeries}
                    excludedAccounts={effectiveExclusions.excludedAccounts}
                    excludedAccountTypes={
                      effectiveExclusions.excludedAccountTypes
                    }
                    excludedCategories={effectiveExclusions.excludedCategories}
                    excludedInstitutions={
                      effectiveExclusions.excludedInstitutions
                    }
                  />
                }
              />
            ))}
        </Box>

        {dialogOpen && (
          <AddAccountModal
            open={dialogOpen}
            onClose={() => {
              handleAddAccountModalClose();
            }}
          />
        )}
        {manageGroupsOpen && (
          <ManageAccountGroupsModal
            open={manageGroupsOpen}
            onClose={() => {
              setManageGroupsOpen(false);
            }}
          />
        )}
        {managePermissionsOpen && (
          <ManageAccountPermissionsModal
            open={managePermissionsOpen}
            onClose={() => {
              handleManagePermissionsClose();
            }}
          />
        )}
        {closedAccountsOpen && (
          <ClosedAccountsModal
            open={closedAccountsOpen}
            onClose={() => {
              handleClosedAccountsClose();
            }}
            onAccountSelect={handleClosedAccountSelect}
            deletedAccountId={deletedClosedAccountId}
            onDelete={(account) => {
              setPendingClosedAccountDeleteId(account.AccountId);
              setSelectedAccount(account);
              setDeleteDialogOpen(true);
            }}
          />
        )}
        {closedAccountFlyoutAccount && (
          <AccountDetailsFlyout
            open={!!closedAccountFlyoutAccount}
            toggleDrawer={() => {
              handleClosedAccountFlyoutClose();
            }}
            account={closedAccountFlyoutAccount}
            showClosedAccountBanner
          />
        )}
        <ConfirmationDialog
          open={deleteDialogOpen}
          onClose={() => {
            handleDeleteDialogClose();
          }}
          onConfirm={handleDeleteConfirm}
          title='Delete Account'
          contentText={`Are you sure you want to delete the account "${selectedAccount?.Name ?? ''}"? This action cannot be undone.`}
          confirmButtonText='Delete'
          confirmButtonColor='error'
          isLoading={isDeleting}
        />
        <MarkAsClosedDialog
          open={markAsClosedDialogOpen}
          onClose={() => {
            setMarkAsClosedDialogOpen(false);
          }}
          onConfirm={handleMarkAsClosedConfirm}
          accountName={selectedAccount?.Name || ''}
          isLoading={isUpdating}
        />
        {selectedAccount && (
          <EditAccountModal
            open={editModalOpen}
            onClose={() => {
              setEditModalOpen(false);
            }}
            account={selectedAccount}
          />
        )}
        {selectedAccount && (
          <UploadAccountHistoryModal
            open={uploadHistoryOpen}
            onClose={() => {
              setUploadHistoryOpen(false);
            }}
            account={selectedAccount}
          />
        )}
      </Box>
    </ContentContainer>
  );
};

export default PortfolioAccounts;
